import { useCallback, useEffect, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const SUITS = ["Denari", "Coppe", "Bastoni", "Spade"];
const DECK = "piacentine";
const empty = { name: "", suit: "Denari", number: 1, title: "", image_url: "", meaning_upright: "", meaning_reversed: "", extra: "" };

export default function AdminPiacentine() {
  const [cards, setCards] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/decks/${DECK}/cards`).then((r) => setCards(r.data)).catch(() => setCards([]));
  }, []);

  useEffect(load, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({
      name: c.name, suit: c.suit, number: c.number, title: c.title || "", image_url: c.image_url || "",
      meaning_upright: c.meaning_upright || "", meaning_reversed: c.meaning_reversed || "", extra: c.extra || "",
    });
    setEditId(c.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, number: Number(form.number) };
      if (editId) await api.put(`/admin/decks/${DECK}/cards/${editId}`, payload);
      else await api.post(`/admin/decks/${DECK}/cards`, payload);
      toast.success("Carta salvata. Le prossime interpretazioni useranno questa versione.");
      setOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa carta dal mazzo?")) return;
    await api.delete(`/admin/decks/${DECK}/cards/${id}`);
    toast.success("Carta eliminata.");
    load();
  };

  return (
    <div data-testid="admin-piacentine-section">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h2 className="font-display text-2xl text-amber-100">Piacentine — base di interpretazione</h2>
        <button onClick={openNew} data-testid="piacentine-card-add-btn" className="gold-btn px-4 py-2 text-xs flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Nuova carta
        </button>
      </div>
      <p className="text-xs text-purple-300/60 mb-8 flex items-center gap-2" data-testid="piacentine-internal-note">
        <EyeOff className="w-4 h-4 text-amber-400/70" />
        Base interna del tuo metodo: non viene mai mostrata ai clienti. Le modifiche diventano subito la nuova base delle interpretazioni.
      </p>

      {cards === null ? null : (
        <div className="space-y-8">
          {SUITS.map((suit) => {
            const suitCards = cards.filter((c) => c.suit === suit);
            if (suitCards.length === 0) return null;
            return (
              <div key={suit}>
                <h3 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400 mb-3">{suit}</h3>
                <div className="space-y-2">
                  {suitCards.map((c) => (
                    <div key={c.id} className="mystic-card px-5 py-3.5 flex items-center justify-between gap-3" data-testid={`piacentine-admin-card-${c.id}`}>
                      <p className="text-sm text-purple-100/85">
                        <span className="text-amber-300 font-medium mr-2">{c.number}</span>
                        {c.name}
                        {c.title && <span className="text-purple-300/60"> — {c.title}</span>}
                      </p>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => openEdit(c)} data-testid={`piacentine-card-edit-${c.id}`} className="p-2 text-purple-300/70 hover:text-amber-300 transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => remove(c.id)} data-testid={`piacentine-card-delete-${c.id}`} className="p-2 text-purple-300/70 hover:text-red-300 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#180a26] border border-amber-500/25 text-purple-50 max-w-xl max-h-[85vh] overflow-y-auto" data-testid="piacentine-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-amber-100">{editId ? "Modifica carta" : "Nuova carta"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <label className="text-xs text-purple-300/60 space-y-1.5 block col-span-1">
                Numero
                <input type="number" min="1" max="10" className="input-dark" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="piacentine-number-input" />
              </label>
              <label className="text-xs text-purple-300/60 space-y-1.5 block col-span-2">
                Seme
                <select value={form.suit} onChange={(e) => setForm({ ...form, suit: e.target.value })} className="input-dark" data-testid="piacentine-suit-select">
                  {SUITS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>
            <input className="input-dark" placeholder="Nome della carta (es. Asso di Denari)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="piacentine-name-input" />
            <input className="input-dark" placeholder="Titolo (es. La Vittoria)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="piacentine-title-input" />
            <input className="input-dark" placeholder="URL immagine (facoltativo)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="piacentine-image-input" />
            <div>
              <p className="text-xs text-purple-300/60 mb-1.5">Significato DRITTA</p>
              <textarea className="input-dark min-h-[80px]" value={form.meaning_upright} onChange={(e) => setForm({ ...form, meaning_upright: e.target.value })} data-testid="piacentine-upright-input" />
            </div>
            <div>
              <p className="text-xs text-purple-300/60 mb-1.5">Significato ROVESCIO (se previsto)</p>
              <textarea className="input-dark min-h-[80px]" value={form.meaning_reversed} onChange={(e) => setForm({ ...form, meaning_reversed: e.target.value })} data-testid="piacentine-reversed-input" />
            </div>
            <div>
              <p className="text-xs text-purple-300/60 mb-1.5">Regole particolari, combinazioni, eccezioni</p>
              <textarea className="input-dark min-h-[70px]" value={form.extra} onChange={(e) => setForm({ ...form, extra: e.target.value })} data-testid="piacentine-extra-input" />
            </div>
            <button onClick={save} disabled={!form.name} data-testid="piacentine-save-btn" className="gold-btn w-full py-3 text-sm disabled:opacity-50">
              Salva carta
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
